import { useEffect, useState } from 'react';
import '../style/Contact.css';
import { GlobeIcon, ChevronDownIcon, BuildingIcon, NetworkIcon, UsersIcon } from '../components/Icons';
import worldMap from '../images/world-map-final.png';

const SERVICES = [
    { name: 'Cyber Security' },
    { name: 'Cloud Services' },
    { name: 'Enterprise Applications' },
    { name: 'ServiceNow' },
    { name: 'AI & Data' },
];

const API = process.env.REACT_APP_API_URL || '';

const EMPTY_FORM = { fullName: '', company: '', email: '', service: '', message: '' };

const Contact = () => {
    const [form, setForm]       = useState(EMPTY_FORM);
    const [status, setStatus]   = useState('idle'); // idle | sending | success | error
    const [errMsg, setErrMsg]   = useState('');

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = 'Contact — KovanTech Solutions';
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrMsg('');
        try {
            const res  = await fetch(`${API}/api/contact`, {
                method:  'POST',
                headers: { 'Content-Type': 'application/json' },
                body:    JSON.stringify(form)
            });
            const data = await res.json();
            if (!res.ok) {
                setErrMsg(data.error || 'Something went wrong. Please try again.');
                setStatus('error');
            } else {
                setStatus('success');
                setForm(EMPTY_FORM);
            }
        } catch {
            setErrMsg('Cannot connect to server. Please try again.');
            setStatus('error');
        }
    };

    return (
        <div className="contact-container">
            <div className="contact-hero">
                <div className="hero-content">
                    <div className="hero-badge">
                        ENTERPRISE SOLUTIONS
                    </div>

                    <h1 className="hero-title white">
                        Connect with Our<br />
                        <span className="title-blue">
                            Strategy Experts
                            <span className="cursor-blink"></span>
                        </span>
                    </h1>

                    <p className="hero-subtitle">
                        Unlock specialized IT consulting tailored for global scale. Our advisory
                        teams bridge the gap between complex infrastructure and business
                        growth.
                    </p>

                    <div className="hero-actions">
                        <a href="#start-project" className="btn-primary-blue">
                            Start Project
                        </a>
                        <a href="#global-hubs" className="btn-outline-hubs">
                            <GlobeIcon /> View Global Hubs
                        </a>
                    </div>
                </div>

                <div className="scroll-indicator">
                    <ChevronDownIcon />
                </div>
            </div>

            {/* Contact Content Section */}
            <div id="start-project" className="contact-content-section">
                <div className="content-grid">
                    {/* Left Column: Form */}
                    <div className="form-column">
                        <div className="column-header">
                            <h2>Initial Consultation</h2>
                            <p>Complete the form below to connect with a regional director. Expect a response within 4 business hours.</p>
                        </div>

                        {status === 'success' ? (
                            <div className="contact-success">
                                <div className="contact-success-icon">✓</div>
                                <h3>Message Sent!</h3>
                                <p>Thank you for reaching out. Our team will respond within 4 business hours.</p>
                                <button
                                    className="submit-btn"
                                    style={{ marginTop: '1.5rem' }}
                                    onClick={() => setStatus('idle')}
                                >
                                    Send Another Inquiry
                                </button>
                            </div>
                        ) : (
                            <form className="contact-form" onSubmit={handleSubmit}>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>FULL NAME</label>
                                        <input
                                            type="text"
                                            name="fullName"
                                            value={form.fullName}
                                            onChange={handleChange}
                                            placeholder="John Doe"
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>COMPANY</label>
                                        <input
                                            type="text"
                                            name="company"
                                            value={form.company}
                                            onChange={handleChange}
                                            placeholder="Enterprise Inc."
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>BUSINESS EMAIL</label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={form.email}
                                        onChange={handleChange}
                                        placeholder="john@enterprise.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>SERVICE INTEREST</label>
                                    <div className="select-wrapper">
                                        <select
                                            name="service"
                                            value={form.service}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select a service</option>
                                            {SERVICES.map((service, index) => (
                                                <option key={index} value={service.name}>{service.name}</option>
                                            ))}
                                            <option value="Other">Other</option>
                                        </select>
                                        <ChevronDownIcon className="select-arrow" />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>PROJECT BRIEF</label>
                                    <textarea
                                        name="message"
                                        value={form.message}
                                        onChange={handleChange}
                                        placeholder="How can our experts assist you?"
                                        rows="4"
                                        required
                                    />
                                </div>

                                {status === 'error' && (
                                    <div className="contact-error">{errMsg}</div>
                                )}

                                <button
                                    type="submit"
                                    className="submit-btn"
                                    disabled={status === 'sending'}
                                >
                                    {status === 'sending' ? 'Sending...' : <>Submit Inquiry <span className="arrow">→</span></>}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Right Column: Map & Locations */}
                    <div id="global-hubs" className="map-column">
                        <div className="column-header">
                            <h2>Global Advisory Hubs</h2>
                            <p>Our physical footprint covers key strategic markets across two continents.</p>
                        </div>
                        <div className="map-visual">
                            <img src={worldMap} alt="World Map with Office Locations" className="map-image" />
                        </div>

                        <div className="locations-list">
                            <a href="https://maps.app.goo.gl/TkrzV46bbPVJQ9wm7" target="_blank" rel="noopener noreferrer" className="location-item">
                                <div className="location-icon-box">
                                    <BuildingIcon />
                                </div>
                                <div className="location-details">
                                    <h3>Head Office</h3>
                                    <p>1710, S Dairy Ashford Rd,<br />Houston, Suite #208<br />TX 77077.</p>
                                </div>
                            </a>

                            <a href="https://maps.app.goo.gl/EfETD4XvMSRp1rBv9" target="_blank" rel="noopener noreferrer" className="location-item">
                                <div className="location-icon-box">
                                    <GlobeIcon />
                                </div>
                                <div className="location-details">
                                    <h3>Nearshore Office</h3>
                                    <p>Oficentro Sabana Sur, 3er piso,<br />Oficina 23 Sabana Sur,<br />San José Costa Rica</p>
                                </div>
                            </a>

                            <a href="https://maps.app.goo.gl/zka4Lki8FNg1BLX1A" target="_blank" rel="noopener noreferrer" className="location-item">
                                <div className="location-icon-box">
                                    <NetworkIcon />
                                </div>
                                <div className="location-details">
                                    <h3>Offshore Office</h3>
                                    <p>5,5/1 Mariyamman Kovil Street,<br />Ganapathy, Coimbatore,<br />Tamil Nadu, India - 641006</p>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            {/* Redefine Legacy CTA Section */}
            <div className="redefine-cta-section">
                <div className="redefine-content">
                    <h2>Ready to redefine your <br /><span className="title-blue">technical legacy?</span></h2>
                    <p>Join our network of industry leaders receiving weekly strategic insights and early access to our proprietary cloud-optimization framework.</p>

                    <div className="cta-actions">
                        <a
                            href="https://www.linkedin.com/company/kovan-technology-solutions"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary-blue"
                        >
                            Join the Network <UsersIcon className="icon-group" />
                        </a>
                        <a
                            href="/kovan-capability-deck.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-outline-hubs"
                            download="Kovan Tech Capability Deck.pdf"
                        >
                            Download Capability Deck
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;
